/**
 * Course definitions (spec section 4).
 *
 * A course is a GENERATOR, not a playlist: pool + difficulty curve + probe
 * weights. Same character and pacing every run, different content every run.
 * Per-course we track win rate and personal best only — runs are not comparable
 * across seeds, so a global board would be meaningless.
 */

import type { CourseDef } from './types';

export const COURSES: CourseDef[] = [
  {
    id: 'world-tour',
    name: 'World Tour',
    description: 'The default run. Everything in the tier pool, evenly paced.',
    pool: {},
    curve: ['easy', 'easy', 'medium', 'medium', 'stinger', 'breather', 'hard'],
    probeWeights: {
      capital: 3,
      region: 2,
      flag: 3,
      currency: 2,
      population: 2,
      borders: 2,
      neighbour: 2,
      language: 1,
      populous: 2,
      languages: 1,
    },
    active: true,
  },
  {
    id: 'island-hopper',
    name: 'Island Hopper',
    description: 'Island nations only. Flags and capitals carry the weight.',
    pool: { islandOnly: true },
    curve: ['easy', 'easy', 'medium', 'stinger', 'breather', 'hard', 'hard'],
    probeWeights: {
      capital: 4,
      flag: 4,
      currency: 3,
      region: 2,
      population: 1,
      populous: 2,
      language: 1,
      borders: 0,
      neighbour: 0,
      languages: 1,
    },
    active: true,
  },
  {
    id: 'landlocked',
    name: 'Landlocked',
    description: 'No coastline anywhere. Border counts become the sharp probe.',
    pool: { landlockedOnly: true },
    curve: ['easy', 'medium', 'medium', 'hard', 'breather', 'stinger', 'hard'],
    probeWeights: {
      capital: 3,
      borders: 4,
      neighbour: 4,
      region: 2,
      flag: 2,
      currency: 2,
      population: 2,
      populous: 1,
      language: 1,
      languages: 1,
    },
    active: true,
  },
  {
    id: 'old-world',
    name: 'Old World',
    description: 'Europe, Africa and Asia. Deep pool, heavy on language ordering.',
    pool: { regions: ['Europe', 'Africa', 'Asia'] },
    curve: ['easy', 'medium', 'stinger', 'medium', 'breather', 'hard', 'hard'],
    probeWeights: {
      capital: 3,
      languages: 3,
      language: 3,
      flag: 2,
      currency: 2,
      borders: 3,
      neighbour: 2,
      population: 2,
      populous: 1,
      region: 1,
    },
    active: true,
  },
  {
    id: 'the-americas',
    name: 'The Americas',
    description: 'Pole to pole down one hemisphere. Capitals and populations.',
    pool: { regions: ['Americas'] },
    curve: ['easy', 'easy', 'medium', 'hard', 'breather', 'stinger', 'medium'],
    probeWeights: {
      capital: 4,
      population: 3,
      populous: 3,
      flag: 2,
      currency: 2,
      borders: 2,
      neighbour: 2,
      region: 1,
      language: 1,
      languages: 1,
    },
    active: true,
  },
  {
    id: 'europe-tight',
    name: 'Europe, Tight',
    description:
      'Small countries packed close together. The pinpoint is the hard part, ' +
      'and the tricolours do the rest.',
    pool: { regions: ['Europe'], maxAreaKm2: 400_000 },
    curve: ['easy', 'medium', 'medium', 'stinger', 'breather', 'hard', 'hard'],
    probeWeights: {
      capital: 3,
      flag: 4,
      currency: 2,
      borders: 3,
      neighbour: 3,
      region: 2,
      population: 2,
      populous: 1,
      language: 1,
      languages: 1,
    },
    active: true,
  },
  {
    id: 'asia-pacific',
    name: 'Asia-Pacific',
    description: 'From the Bosphorus to the dateline. Populations swing wildly here.',
    pool: { regions: ['Asia', 'Oceania'] },
    curve: ['easy', 'medium', 'stinger', 'medium', 'breather', 'hard', 'hard'],
    probeWeights: {
      capital: 3,
      population: 4,
      populous: 3,
      flag: 3,
      currency: 2,
      region: 2,
      borders: 2,
      neighbour: 2,
      language: 2,
      languages: 2,
    },
    active: true,
  },
  {
    id: 'africa-54',
    name: 'Africa 54',
    description: 'The continent most players guess at. Pan-African flags punish that.',
    pool: { regions: ['Africa'] },
    curve: ['easy', 'easy', 'medium', 'hard', 'breather', 'stinger', 'hard'],
    probeWeights: {
      capital: 4,
      flag: 4,
      currency: 2,
      borders: 3,
      neighbour: 3,
      region: 2,
      population: 2,
      populous: 1,
      language: 2,
      languages: 1,
    },
    active: true,
  },
];

export function courseById(id: string): CourseDef | undefined {
  return COURSES.find((c) => c.id === id);
}
